import { deviceSize, type Size, type UiView } from "odyssey-shared";
import { keptInterfaceStep } from "./display-settings.ts";
import { isTouchPage, watchTouchPage } from "./touch/page.ts";

/**
 * The window's size in device pixels (ADR-061, "the scale is computed in device pixels"). Both scales and
 * the canvas's backing store are computed from it, never from CSS pixels, which operating-system scaling
 * such as 125 percent makes fractional. A `ResizeObserver` reports the host's `device-pixel-content-box`
 * where the browser supports that box; elsewhere the size is `round(innerWidth x devicePixelRatio)` and
 * the same for the height. Browser zoom and a move to another monitor change `devicePixelRatio` without
 * necessarily changing the device size, so a `matchMedia('(resolution: Ndppx)')` query, re-armed at each
 * new ratio, reports those too.
 */

/**
 * The window in device pixels, the ratio of device pixels to CSS pixels it was measured at, and whether the page
 * is a touch page (`touch`, from `UiView`), which the UI scale reads (`phoneUiScale`).
 */
export interface DeviceView extends UiView {
  readonly ratio: number;
}

/**
 * The window's device size from its CSS size and the current ratio.
 *
 * @returns The fallback measure the amendment names, with the ratio and whether the page is a touch page.
 */
export function measureWindow(): DeviceView {
  const ratio = window.devicePixelRatio || 1;
  const size = deviceSize(window.innerWidth, window.innerHeight, ratio);
  return { width: size.width, height: size.height, ratio, touch: isTouchPage(), ...stepOf() };
}

/**
 * The viewer's Interface size as a view's `step` (held by `keptInterfaceStep`, read from the store once and kept current), left out at Auto, so every page that measures the window, the
 * gallery's included, draws the scale the viewer chose; the game page's render resolution then holds the live value.
 *
 * @returns The `step` property, or nothing at Auto.
 */
function stepOf(): { readonly step?: number } {
  const step = keptInterfaceStep();
  return step === 0 ? {} : { step };
}

/**
 * Reads the device-pixel box an observer entry reports, if the browser reports one.
 *
 * @param entry The observer entry.
 * @returns The box's size in device pixels, or null where the browser does not report it.
 */
function devicePixelBox(entry: ResizeObserverEntry): Size | null {
  const box = (entry as Partial<Pick<ResizeObserverEntry, "devicePixelContentBoxSize">>).devicePixelContentBoxSize?.[0];
  return box === undefined ? null : { width: box.inlineSize, height: box.blockSize };
}

/**
 * The device box to trust: the browser's own report unless it contradicts the element's CSS size at the current ratio, as
 * Chromium's touch emulation does at a ratio above 1 (it reports the CSS size as the device size), in which case the CSS
 * size times the ratio, rounded. A real display's box is within a pixel of that product.
 *
 * @param box The browser's device-pixel box, or null where it reports none.
 * @param css The element's content box in CSS pixels.
 * @param ratio The device pixel ratio.
 * @returns The device size, or null when there is no box and the window's own measure applies.
 */
export function trustedDeviceBox(box: Size | null, css: Size, ratio: number): Size | null {
  if (box === null) {
    return null;
  }
  const expected = { width: Math.round(css.width * ratio), height: Math.round(css.height * ratio) };
  return Math.abs(box.width - expected.width) <= 2 && Math.abs(box.height - expected.height) <= 2 ? box : expected;
}

/**
 * Watches the device size of the element the canvas fills, and the device pixel ratio, calling back
 * whenever either changes. The first observer report is always passed on, so a caller that started from
 * `measureWindow()` is corrected to the exact device box as soon as layout has run.
 *
 * @param host The element the canvas fills.
 * @param onChange Called with the new view each time it differs from the last one passed on.
 * @returns A function that stops watching.
 */
export function watchDevice(host: HTMLElement, onChange: (view: DeviceView) => void): () => void {
  let last: DeviceView | null = null;
  let box: Size | null = null;
  let query: MediaQueryList | null = null;

  /**
   * Passes a view on unless it matches the last one.
   *
   * @param size The device size.
   */
  const emit = (size: Size): void => {
    const ratio = window.devicePixelRatio || 1;
    const touch = isTouchPage();
    if (last !== null && last.width === size.width && last.height === size.height && last.ratio === ratio && last.touch === touch) {
      return;
    }
    last = { width: size.width, height: size.height, ratio, touch, ...stepOf() };
    onChange(last);
  };

  const observer = new ResizeObserver((entries) => {
    const entry = entries[entries.length - 1];
    if (entry === undefined) {
      return;
    }
    const css = entry.contentBoxSize[0];
    box = css === undefined ? devicePixelBox(entry) : trustedDeviceBox(devicePixelBox(entry), { width: css.inlineSize, height: css.blockSize }, window.devicePixelRatio || 1);
    emit(box ?? measureWindow());
  });
  try {
    observer.observe(host, { box: "device-pixel-content-box" });
  } catch {
    observer.observe(host);
  }

  /**
   * Re-measures at a new ratio and listens for the next one. The last device box stays right when only
   * the ratio moved, as under browser zoom; when the device size moved as well, the observer reports it.
   */
  const onRatio = (): void => {
    arm();
    emit(box ?? measureWindow());
  };

  /**
   * Listens for the ratio to move away from its current value.
   */
  const arm = (): void => {
    query?.removeEventListener("change", onRatio);
    query = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    query.addEventListener("change", onRatio);
  };

  arm();
  const stopTouch = watchTouchPage(() => emit(box ?? measureWindow()));

  return () => {
    observer.disconnect();
    stopTouch();
    query?.removeEventListener("change", onRatio);
    query = null;
  };
}
